import { api } from "@/lib/api";

// Live "Edit Site Text" overrides: swaps exact-match UI strings (text nodes + placeholder/title/aria-label)
// across the whole site without touching component code. React re-renders are re-patched by the observer.
let map = new Map();
let spots = new Map(); // original -> [{ s, n }] overrides that apply to one place only
let observer = null;
// Remember what we changed so overrides can be undone (preview off / revert) and never re-patch in a loop.
const patchedText = new Map(); // node -> { orig, set }
const patchedAttr = new Map(); // element -> { [attr]: { orig, set } }
const ATTRS = ["placeholder", "title", "aria-label"];
const SKIP = new Set(["SCRIPT", "STYLE", "TEXTAREA", "CODE", "PRE"]);

const skipped = (el) => !!(el && el.closest && el.closest("[data-site-text-skip]"));
const route = () => "/" + (window.location.pathname.split("/")[1] || "");
// Stable "spot" id for one place on the site: nearest data-testid (or DOM path) + top-level page.
export function spotKeyFor(el) {
  const tid = el?.closest?.("[data-testid]");
  if (tid) return `${route()}#${tid.getAttribute("data-testid")}`;
  const parts = [];
  for (let e = el; e && e.nodeType === 1 && e !== document.body && parts.length < 8; e = e.parentElement) {
    parts.unshift(`${e.tagName.toLowerCase()}:${[...(e.parentElement?.children || [])].indexOf(e)}`);
  }
  return `${route()}>${parts.join(">")}`;
}
function lookup(t, el) {
  const list = spots.get(t);
  if (list && el) { const k = spotKeyFor(el); const hit = list.find((x) => x.s === k); if (hit) return hit.n; }
  return map.get(t);
}
export function originalText(node) {
  const r = patchedText.get(node);
  return r && node.nodeValue === r.set ? r.orig : node.nodeValue;
}
export function originalAttr(el, a) {
  const r = patchedAttr.get(el)?.[a];
  return r && el.getAttribute(a) === r.set ? r.orig : el.getAttribute(a);
}
function patchText(node) {
  if (skipped(node.parentElement)) return;
  const v = node.nodeValue;
  if (!v || v.length > 600) return;
  const rec = patchedText.get(node);
  if (rec && v === rec.set) return; // already ours
  const t = v.trim();
  if (!t) return;
  const n = lookup(t, node.parentElement);
  if (n != null && n !== t) { const set = v.replace(t, n); patchedText.set(node, { orig: v, set }); node.nodeValue = set; }
}
function patchEl(el) {
  if (SKIP.has(el.tagName) || el.isContentEditable || skipped(el)) return;
  for (const a of ATTRS) {
    const v = el.getAttribute(a);
    if (!v) continue;
    const rec = patchedAttr.get(el)?.[a];
    if (rec && v === rec.set) continue;
    const n = lookup(v.trim(), el);
    if (n != null && n !== v) { patchedAttr.set(el, { ...(patchedAttr.get(el) || {}), [a]: { orig: v, set: n } }); el.setAttribute(a, n); }
  }
}
function walk(root) {
  if ((!map.size && !spots.size) || !root) return;
  if (root.nodeType === 3) { if (!SKIP.has(root.parentNode?.tagName)) patchText(root); return; }
  if (root.nodeType !== 1 || SKIP.has(root.tagName)) return;
  patchEl(root);
  const tw = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
    acceptNode: (n) => (n.nodeType === 1 && (SKIP.has(n.tagName) || n.isContentEditable) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
  });
  for (let n = tw.nextNode(); n; n = tw.nextNode()) { if (n.nodeType === 3) patchText(n); else patchEl(n); }
}

function restoreAll() {
  patchedText.forEach((r, node) => { if (node.isConnected && node.nodeValue === r.set) node.nodeValue = r.orig; });
  patchedAttr.forEach((attrs, el) => Object.entries(attrs).forEach(([a, r]) => { if (el.isConnected && el.getAttribute(a) === r.set) el.setAttribute(a, r.orig); }));
  patchedText.clear(); patchedAttr.clear();
}

export function setSiteTextOverrides(list) {
  restoreAll();
  const valid = (list || []).filter((x) => x && x.o && x.n);
  map = new Map(valid.filter((x) => !x.s).map((x) => [x.o.trim(), x.n]));
  spots = new Map();
  valid.filter((x) => x.s).forEach((x) => { const k = x.o.trim(); spots.set(k, [...(spots.get(k) || []), { s: x.s, n: x.n }]); });
  if (!observer && typeof MutationObserver !== "undefined") {
    observer = new MutationObserver((muts) => {
      if (!map.size && !spots.size) return;
      for (const m of muts) {
        if (m.type === "characterData") patchText(m.target);
        else if (m.type === "attributes") patchEl(m.target);
        else m.addedNodes.forEach(walk);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
  }
  walk(document.body);
}

export const SITE_TEXT_PREVIEW_KEY = "cm_site_text_preview";

export async function initSiteText() {
  try {
    if (localStorage.getItem(SITE_TEXT_PREVIEW_KEY) === "1") {
      const d = await api.adminSiteText().catch(() => null);
      if (d) { setSiteTextOverrides(d.draft || []); return; } // admin previewing unpublished drafts
    }
    const { overrides } = await api.siteText();
    setSiteTextOverrides(overrides);
  } catch { /* site keeps its built-in text */ }
}
